// Phase P.30 — Full ledger audit history dialog.
//
// Fetches every status_event that touches any row on the given ledger and
// renders a chronological timeline: [icon] Actor VERB entity-summary · time,
// with a before/after diff for edits.
//
// The `variant` prop picks the fetch URL + entity-type map:
//   - "staff":   /staff/{ownerId}/ledger-history
//   - "manager": /manager-ledger/history
//   - "cash":    /cash-holdings/history
//
// Every event row is actor-attributed. When the actor is a background job
// (e.g. auto-created from a paid invoice), we render "Auto · system".

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatBookingStamp, formatEventTime } from "@/lib/dates";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Clock, History as HistoryIcon, Plus, Pencil, Trash2, CheckCircle2,
  Banknote, RotateCw,
} from "lucide-react";

const VERB_META = {
  created:  { icon: Plus,        label: "created",  tint: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  updated:  { icon: Pencil,      label: "edited",   tint: "text-amber-700  bg-amber-50  border-amber-200"  },
  deleted:  { icon: Trash2,      label: "deleted",  tint: "text-rose-700   bg-rose-50   border-rose-200"   },
  settled:  { icon: CheckCircle2,label: "settled",  tint: "text-sky-700    bg-sky-50    border-sky-200"    },
  lodged:   { icon: Banknote,    label: "lodged",   tint: "text-violet-700 bg-violet-50 border-violet-200" },
};

function pickMeta(status) {
  return VERB_META[status] || { icon: RotateCw, label: status || "event", tint: "text-slate-600 bg-slate-50 border-slate-200" };
}

const fmtMoney = (n, ccy = "EUR") => {
  const num = Number(n || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy, currencyDisplay: "narrowSymbol" }).format(num);
  } catch { return `€${num.toFixed(2)}`; }
};

function summarize(ev, variant) {
  const meta = ev.meta || {};
  const kind = meta.kind || meta.entry_type;
  // Settlement rows.
  if (ev.entity_type === "staff_settlement" || ev.entity_type === "manager_settlement") {
    return `Settle-to-zero · ${fmtMoney(meta.amount_settled, meta.currency)} · ${meta.method || "method?"}`
      + (meta.ledger_entry_ids ? ` · ${meta.ledger_entry_ids.length} entries` : "");
  }
  // Cash-holding rows.
  if (ev.entity_type === "cash_holding") {
    if (ev.to_status === "lodged") {
      return `Lodged ${fmtMoney(meta.amount, "EUR")}` + (meta.lodged_reference ? ` · ref ${meta.lodged_reference}` : "");
    }
    return `${fmtMoney(meta.amount, "EUR")}${meta.customer_name ? ` · ${meta.customer_name}` : ""}${meta.booking_code ? ` · ${meta.booking_code}` : ""}`;
  }
  // Ledger entry rows (staff or manager).
  const bits = [];
  if (kind) bits.push(kind);
  if (meta.amount) bits.push(fmtMoney(meta.amount, meta.currency));
  if (meta.customer_name) bits.push(meta.customer_name);
  if (meta.booking_code) bits.push(meta.booking_code);
  if (meta.linked_invoice_number) bits.push(meta.linked_invoice_number);
  if (meta.category) bits.push(meta.category);
  return bits.filter(Boolean).join(" · ") || (ev.note || ev.to_status);
}

function diffRows(before, after) {
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const rows = [];
  keys.forEach((k) => {
    if (JSON.stringify(before[k]) === JSON.stringify(after[k])) return;
    rows.push({ k, before: before[k], after: after[k] });
  });
  return rows;
}

export default function LedgerFullHistoryDialog({
  open,
  onOpenChange,
  variant,           // "staff" | "manager" | "cash"
  ownerId,           // staff_id for "staff"; ignored for the others
  ledgerLabel,       // human string for the dialog header ("John Doyle", "My cash ledger", "Company Cash Hold")
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const url = useMemo(() => {
    if (variant === "staff") return `/staff/${ownerId}/ledger-history`;
    if (variant === "manager") return "/manager-ledger/history";
    if (variant === "cash") return "/cash-holdings/history";
    return null;
  }, [variant, ownerId]);

  useEffect(() => {
    if (!open || !url) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    api.get(url, { params: { limit: 500 } })
      .then((r) => {
        if (cancelled) return;
        const evs = Array.isArray(r.data?.events) ? r.data.events : (Array.isArray(r.data) ? r.data : []);
        // Newest first for the panel.
        evs.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
        setRows(evs);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || "Unable to load history");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, url]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[92vw] max-h-[85vh] overflow-y-auto" data-testid="ledger-full-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HistoryIcon className="h-5 w-5" /> Full history · {ledgerLabel}
          </DialogTitle>
          <DialogDescription>
            Every actor-attributed event on this ledger: entries created,
            edited, deleted, settled, and (for the cash hold) lodged.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="ledger-full-history-loading">
            <Clock className="inline h-4 w-4 mr-1 animate-spin" /> Loading…
          </div>
        )}
        {err && !loading && (
          <div className="text-sm text-rose-700 py-3" data-testid="ledger-full-history-error">{err}</div>
        )}
        {!loading && !err && rows.length === 0 && (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="ledger-full-history-empty">
            No history yet.
          </div>
        )}
        {!loading && !err && rows.length > 0 && (
          <ol className="relative border-l border-border ml-2 mt-2 space-y-4" data-testid="ledger-full-history-list">
            {rows.map((ev) => {
              const m = pickMeta(ev.to_status);
              const Icon = m.icon;
              const diffs = diffRows(ev.meta?.before, ev.meta?.after);
              return (
                <li key={ev.id} className="ml-3 relative" data-testid={`ledger-full-history-row-${ev.id}`} data-event-to={ev.to_status}>
                  <span className={`absolute -left-[19px] top-0 inline-flex h-5 w-5 items-center justify-center rounded-full border ${m.tint}`}>
                    <Icon className="h-3 w-3" />
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span className="font-semibold">
                      {ev.actor_name || (ev.actor_role === "system" ? "Auto · system" : (ev.actor_role || "system"))}
                    </span>
                    <span className="text-muted-foreground">{m.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {ev.entity_type?.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="text-sm mt-0.5">{summarize(ev, variant)}</div>
                  <div className="text-[11px] text-muted-foreground mono mt-0.5" title={ev.at}>
                    {formatBookingStamp(ev.at)} · {formatEventTime(ev.at)}
                  </div>
                  {ev.note && (
                    <div className="text-xs text-muted-foreground italic mt-1">{ev.note}</div>
                  )}
                  {diffs.length > 0 && (
                    <div className="mt-1.5 border border-border rounded-md p-2 bg-muted/30 text-[11px] mono space-y-0.5" data-testid={`ledger-full-history-row-${ev.id}-diff`}>
                      {diffs.map((d) => (
                        <div key={d.k} className="flex flex-wrap items-baseline gap-2">
                          <span className="font-bold">{d.k}</span>
                          <span className="line-through text-rose-700">{String(d.before ?? "—")}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="text-emerald-700">{String(d.after ?? "—")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        <div className="flex justify-end pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ledger-full-history-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
